export const translations = {
  meta: {
    title: 'Learn Angular testing with Jest and Testing Library',
    description:
      'A real story about how testing can change your career. Learn Angular testing with Jest and Testing Library in a practical and modern way. Complete guide for developers.',
  },
  sections: {
    bookCover: {
      title: 'Testing in Angular',
      description:
        'Learn how to implement effective unit tests in your Angular applications using Jest and Testing Library. This comprehensive guide will take you from basic concepts to advanced techniques.',
      features: [
        'Step-by-step guide from zero to advanced',
        'Practical examples and real-world use cases',
        'Complete coverage of Jest and Testing Library',
        'Best practices and testing patterns',
      ],
    },
    whatIsTesting: {
      title: 'What is Unit Testing?',
      content:
        'Unit testing is a software testing method where individual units or components of a software are tested in isolation. In Angular, this means testing individual components, services, and other features to ensure they work as expected. Unit tests help catch bugs early in the development process and provide confidence when making changes to the codebase.',
    },
    whyJestAndTestingLibrary: {
      title: 'Why Jest and Testing Library?',
      content:
        'Jest is a powerful testing framework that provides a complete solution for testing JavaScript applications. When combined with Testing Library, it offers a user-centric approach to testing Angular components. This combination allows you to write tests that focus on how users interact with your application, making your tests more maintainable and less brittle.',
    },
    keyFeatures: {
      title: 'Key Features',
      items: [
        {
          title: 'Easy Setup',
          description: 'Quick and straightforward configuration of Jest and Testing Library in your Angular project',
        },
        {
          title: 'Component Testing',
          description: 'Comprehensive guide to testing Angular components with real-world examples',
        },
        {
          title: 'Service Testing',
          description: 'Learn how to effectively test services, including HTTP calls and dependencies',
        },
        {
          title: 'Best Practices',
          description: 'Follow industry best practices for writing maintainable and effective tests',
        },
      ],
    },
    dondeComprar: {
      title: 'Where to buy the book?',
      description:
        'You can now get "Testing in Angular" directly on Amazon or Leanpub. Choose your preferred platform to buy it in Kindle, print, or PDF format.',
      button: 'Buy on Amazon',
      buttonLeanpub: 'Buy on Leanpub',
    },
    cursoUdemy: {
      title: 'Did you know that Angular 20 has completely changed the way to develop web applications?',
      description: `Forget about outdated methods, slow code, or solutions that no longer work. In my Angular Moderno course, you will learn the most current and efficient techniques that will make you a future-proof Angular developer.`,
      features: [
        '✅ Learn with Angular 20: Signals, Standalone Components, @if, @for, and much more.',
        '✅ Real practical projects: Don\'t get lost in theory, learn by doing.',
        '✅ Constant updates: Lifetime access to future framework updates.',
        '🔴 Limited offer! The course will soon increase in price. Secure your spot now and save money.',
      ],
      button: 'Go to the course on Udemy',
    },
  },
  navigation: {
    home: 'Home',
    reality: 'The Reality',
    features: 'Features',
    testimonials: 'Testimonials',
    chapters: 'Chapters',
    blog: 'Blog',
    videos: 'Videos',
  },
  all: {
    ctaButton: 'Buy e-book Now',
  },
  hero: {
    title: 'Can you imagine losing your dream job for not knowing how to write a test?',
    subtitle: 'It happened to me.',
    description:
      "It was in a technical interview, one of those that tests you with real-time live coding. Everything was going well... until it came time to write unit tests. I froze. It wasn't lack of experience or business knowledge. I simply had never taken it seriously.",
    story: 'Testing was that task I always left for "later". And that "later" cost me dearly.',
    promise:
      'I left that interview with a promise. That I would never underestimate testing again. That I would learn it thoroughly. But above all, that everything I learned I would share. Because no one deserves to lose an opportunity like that for not being prepared for something so essential.',
    secondaryButton: 'View content',
  },
  reality: {
    title: "Testing doesn't slow down. It accelerates.",
    description:
      'For years, unit testing in the frontend has been a postponed task. In many organizations, even with experienced teams, it has been assumed that testing components, services or forms was optional. The excuses have always been the same: lack of time, urgent deliveries, pressure to launch new features or simply the belief that "manual testing is enough".',
    consequences:
      'But this mentality has consequences: regressions in production, errors discovered late, fragile components in the face of any refactor, and technical debt that grows silently... until it explodes.',
    reality:
      'In a world where the frontend has stopped being "the pretty layer" and has become a critical piece of the system, not having automated tests is, literally, playing with fire.',
    benefits: [
      {
        title: 'Confidence to refactor',
        description: 'When done well, it gives confidence to refactor without fear',
      },
      {
        title: 'New features without fear',
        description: "Add new functionality knowing you won't break existing code",
      },
      {
        title: 'Safer code',
        description: 'Ensures that what worked yesterday will continue working tomorrow',
      },
      {
        title: 'Modern tools',
        description: 'Angular has changed: Signals, standalone components, Testing Library, Jest',
      },
    ],
  },
  features: {
    title: "What you'll learn",
    description:
      'This e-book will provide you with practical knowledge that you can immediately apply in your Angular projects',
    items: [
      {
        title: 'Setting up Jest in Angular',
        description: 'Learn how to integrate Jest into your Angular project from scratch',
      },
      {
        title: 'Testing Library and its advantages',
        description: 'Discover why Testing Library is the best option for your component tests',
      },
      {
        title: 'Testing Signals in Angular',
        description: 'Master testing the new Signals API in Angular',
      },
      {
        title: 'Service mocking',
        description: 'Effective techniques for creating mocks of services and dependencies',
      },
      {
        title: 'Pipe testing',
        description: 'Learn how to test custom and built-in Angular pipes',
      },
      {
        title: 'Test Driven Development',
        description: 'Implement TDD in your daily workflow',
      },
    ],
  },
  testimonials: {
    title: 'What developers are saying',
    items: [
      {
        name: 'Maria Gonzalez',
        role: 'Senior Frontend Developer',
        company: 'Tech Solutions',
        comment: 'This guide transformed my approach to testing in Angular. Now my applications are much more robust.',
      },
      {
        name: 'Carlos Rodriguez',
        role: 'Tech Lead',
        company: 'Innovative Apps',
        comment:
          'The chapter on Signals in Angular is especially valuable. Information hard to find in other resources.',
      },
      {
        name: 'Laura Perez',
        role: 'Angular Developer',
        company: 'Digital Services',
        comment: 'I finally understand how to properly implement TDD in my Angular projects. Highly recommended.',
      },
    ],
  },
  videos: {
    title: 'Complementary videos',
    description: 'Reinforce your learning with these tutorial videos from the Dominicode channel',
  },
  content: {
    title: "It's not just about covering a pending subject",
    description:
      "It's about writing safer, more maintainable and more professional code. This book was born as a response to that reality. Not as a theoretical manifesto, but as a practical, modern guide with feet on the ground. Designed for those who build real applications with Angular.",
    purpose:
      'Because yes: testing in the frontend is possible, profitable and, above all, a concrete way to save time, reduce errors and improve software quality.',
    formTitle: 'Buy the e-book',
    nameLabel: 'Name',
    emailLabel: 'Email',
    submitButton: 'I want it',
    privacyNotice: "We won't share your information with third parties.",
    features: [
      '✅ Practical and modern guide for real applications',
      '✅ Testing with Signals and standalone components',
      '✅ Current tools: Jest and Testing Library',
      '✅ Safer, maintainable and professional code',
    ],
  },
  footer: {
    copyright: '© 2025 DOMINICODE, LLC. All rights reserved.',
    links: {
      privacy: 'Privacy Policy',
      terms: 'Terms and Conditions',
      contact: 'Contact',
    },
    description:
      'Advanced resources for testing in Angular with Jest and Testing Library. Improve the quality of your code with efficient unit tests.',
    resources: {
      title: 'Resources',
      testingLibrary: 'Testing Library Docs',
      jest: 'Jest Docs',
    },
  },
  faq: {
    title: 'Frequently Asked Questions',
    audience: {
      question: 'Who is this ebook for?',
      answer:
        'This ebook is aimed at intermediate-level Angular developers who want to learn testing. It is also ideal for senior developers with experience who want to perfect their Unit Testing skills with Jest and Testing Library.',
    },
    prerequisites: {
      question: 'What prerequisites do I need to understand the ebook?',
      answer:
        'You need to have basic knowledge of JavaScript, TypeScript, and the Angular framework. No previous testing experience is required; the ebook will guide you from scratch!',
    },
    tools: {
      question: 'What tools are covered in the ebook?',
      answer:
        'The ebook focuses specifically on Jest as a test runner and Testing Library (specifically @testing-library/angular) for writing tests in a way that promotes good practices and more robust results.',
    },
    integration: {
      question: 'Does this ebook cover integration or end-to-end testing?',
      answer:
        'No, this ebook focuses exclusively on Unit Testing. It centers on how to test components, services, and other code units in isolation. Additionally, you will learn to focus your tests on how your users interact with the application.',
    },
    learn: {
      question: 'What exactly will I learn with this ebook?',
      items: {
        setup: 'Set up your testing environment with Jest and Testing Library in an Angular project.',
        components: 'Write unit tests for Angular components, services, pipes, and directives.',
        interactions: 'Effectively simulate user interactions with Testing Library.',
        dependencies: 'Handle dependencies and mocks cleanly.',
        maintainable: 'Write tests that are maintainable and resistant to refactoring.',
        bestPractices: 'Apply testing best practices to improve your code quality.',
      },
    },
    examples: {
      question: 'Does the ebook include code examples or sample projects?',
      answer:
        'Yes, the ebook is full of practical code examples. The idea is that you can follow the examples and apply them directly to your own projects.',
    },
    updates: {
      question: 'Will I receive updates if the tools evolve?',
      answer:
        'Yes, the ebook will be periodically updated to reflect changes and best practices in Angular, Jest, and Testing Library. You will have access to these updates at no additional cost.',
    },
    format: {
      question: 'What is the format of the ebook?',
      answer:
        'The ebook will be available in PDF format and also on Amazon Kindle so you can read it on your favorite device.',
    },
    quality: {
      question: 'Will this ebook help me improve my Angular code quality?',
      answer:
        'Definitely. By learning to write effective unit tests, you will not only ensure your code works as expected but also improve its design, modularity, and maintainability.',
    },
  },
  chapters: {
    sectionTitle: 'Ebook Content',
    chapter1: {
      title: 'Chapter I',
      subtitle: 'Fundamentals of Modern Angular Testing',
      description: 'Learn the basic concepts and fundamentals to start with testing in Angular.',
    },
    chapter2: {
      title: 'Chapter II',
      subtitle: 'Unit Testing with Signals and Standalone Components',
      description: 'Master unit testing using the latest Angular features.',
    },
    chapter3: {
      title: 'Chapter III',
      subtitle: 'Services, Complex Logic, and Mocks',
      description: 'Dive deep into service testing and effective mock implementation.',
    },
    chapter4: {
      title: 'Chapter IV',
      subtitle: 'Forms, Directives, and Other Common Elements',
      description: 'Learn how to test forms, directives, and other common Angular components.',
    },
    chapter5: {
      title: 'Chapter V',
      subtitle: 'Best Practices and Common Cases',
      description: 'Discover best practices and how to handle common testing scenarios.',
    },
    chapter6: {
      title: 'Chapter VI',
      subtitle: 'Modern Alternative: Testing in Angular with Vitest',
      description: 'Discover Vitest, a modern and fast alternative to Jest, perfect for Angular projects with Vite.',
    },
  },
  emailSubscription: {
    title: 'Interested in the eBook?',
    description:
      'The book is already available and the course is in the oven.\n\n You will be the first to know and receive exclusive content.',
    placeholder: 'Your email',
    button: 'I want the notice',
    loading: 'Sending...',
    successMessage: 'Thank you for subscribing!',
    errorMessage: 'There was an error saving your email. Please try again.',
    duplicateMessage: 'You are already on the list! We will notify you when the ebook is available.',
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdate: 'Last updated: June 15, 2025',
    responsibleTitle: '1. Data Controller',
    responsible: 'DOMINICODE, LLC, contact e-mail: privacy@dominicode.com.',
    dataTitle: '2. Data we collect',
    data: 'Contact information (name, e-mail) when you purchase or subscribe.\n\nBrowsing data through cookies and similar technologies (e.g., Meta Pixel) that record visits, clicks, and purchases.\n\nMarketing preferences and email opening metrics.',
    purposesTitle: '3. Purposes and legal basis',
    purposesTable: [
      {
        purpose: 'To process your purchase and send the eBook',
        legal: 'Execution of a contract (art. 6 b GDPR)',
        retention: '5 years or as required by tax law',
      },
      {
        purpose: 'To send newsletters and promotions',
        legal: 'Express consent (art. 6 a)',
        retention: 'Until you withdraw your consent',
      },
      {
        purpose: 'Analytics and targeted advertising with Meta Pixel',
        legal: 'Express consent (art. 6 a)',
        retention: '26 months',
      },
    ],
    cookiesTitle: '4. Cookies and tracking technologies',
    cookies:
      "This site uses Meta Pixel from Meta Platforms Inc. (1601 Willow Rd, Menlo Park, CA 94025, USA) to measure conversions and display relevant ads on Facebook and Instagram.\nBy accepting marketing cookies:\n\nYour IP, user agent, and events (e.g., PageView, Purchase) are sent, which may be transferred to the USA under the EU's standard contractual clauses.\n\nYou can revoke your consent at any time from the cookie banner or by writing to privacy@dominicode.com.\n\nMore information in Meta's Data Policy.\nwww.facebook.com/policy.php",
    recipientsTitle: '5. Recipients',
    recipients:
      'Meta Platforms Inc. (Advertising and analytics)\n\nEmail marketing provider mailerlite\n\nPayment gateway Stripe (tokenized; we do not store card data)\n\nAll our providers have signed data processing agreements in accordance with art. 28 GDPR.',
    rightsTitle: '6. User rights',
    rights:
      'You can exercise your rights of access, rectification, erasure, opposition, limitation, or portability by sending an email to privacy@dominicode.com with a copy of your ID or equivalent document. If you are not satisfied, you have the right to file a complaint with the competent data protection authority.',
    securityTitle: '7. Security',
    security: 'We apply TLS encryption, access control, and periodic audits to protect your information.',
    changesTitle: '8. Changes',
    changes:
      'We reserve the right to modify this Policy to adapt it to new legislation or operational developments. We will publish any changes at least 10 days in advance.',
    entity: 'DOMINICODE, LLC (Delaware LLC, EIN: 32-0804117)',
  },
};
